// src/admin/components/kits/KitBatchCard.jsx
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import RedeemableCodeStatusBadge, {
  redeemableCodeStatusLabel,
} from '../../../shared/components/RedeemableCodeStatusBadge';
import CollapsibleCard from '../common/CollapsibleCard';
import DataTable from '../common/DataTable';
import { useKitBatchShortUrls } from '../../hooks/useKitBatches';
import { formatDate } from '../../../shared/utils/formatDate';

export default function KitBatchCard({ batch, onExport }) {
  const {
    shortUrls,
    loading,
    loaded,
    fetchShortUrls,
  } = useKitBatchShortUrls(batch.batch_number);

  const handleExpand = useCallback(() => {
    if (!loaded) {
      fetchShortUrls();
    }
  }, [loaded, fetchShortUrls]);

  const kitCodeColumns = useMemo(
    () => [
      {
        key: 'code',
        label: 'Code',
        sortable: true,
        render: (url) => <code>{url.code}</code>,
        filterValue: (url) => url.code || '',
      },
      {
        key: 'kit',
        label: 'Kit',
        sortable: true,
        render: (url) =>
          url.kit ? (
            <>
              <span className="fw-semibold">{url.kit.name}</span>
              {url.kit.sku && (
                <span className="text-muted small ms-1">({url.kit.sku})</span>
              )}
            </>
          ) : (
            <span className="text-muted">—</span>
          ),
        filterValue: (url) => [url.kit?.name, url.kit?.sku].filter(Boolean).join(' '),
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        render: (url) => (
          <RedeemableCodeStatusBadge
            domain="kit"
            invalidated={!!url.invalidated_at}
            consumed={!!url.used_at}
          />
        ),
        filterValue: (url) =>
          redeemableCodeStatusLabel('kit', !!url.invalidated_at, !!url.used_at),
      },
    ],
    []
  );

  const header = (
    <div className="d-flex align-items-center justify-content-between w-100 pe-2">
      <div className="d-flex flex-column gap-1">
        <div>
          <span className="fw-semibold">
            {batch.name || (batch.kit ? `${batch.kit.name} codes` : 'Kit code batch')}
          </span>
          <span className="text-muted ms-2 small">{batch.batch_number}</span>
        </div>
        <div className="text-muted small d-flex align-items-center gap-3">
          {batch.kit && (
            <span>
              <i className="bi bi-box-seam me-1" />
              {batch.kit.name}
              {batch.kit.sku && <span className="ms-1">({batch.kit.sku})</span>}
            </span>
          )}
          <span>
            <i className="bi bi-calendar me-1" />
            {formatDate(batch.created_at)}
          </span>
          <span className="text-primary fw-medium">
            {batch.counts?.total || 0} codes
          </span>
        </div>
      </div>
      <button
        className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
        onClick={(e) => {
          e.stopPropagation();
          onExport(batch.batch_number);
        }}
        type="button"
      >
        <i className="bi bi-download" />
        Export CSV
      </button>
    </div>
  );

  return (
    <CollapsibleCard
      header={header}
      onExpand={handleExpand}
      isLoading={loading}
      isEmpty={loaded && shortUrls.length === 0}
      emptyMessage="No kit codes in this batch."
      variant="table"
    >
      <div className="table-responsive">
        <DataTable
          items={shortUrls}
          columns={kitCodeColumns}
          filterPlaceholder="Search codes in this batch…"
          emptyMessage="No kit codes in this batch."
          hideFilter={shortUrls.length < 5}
          getRowKey={(url) => url.id}
          className="mb-0"
        />
      </div>
    </CollapsibleCard>
  );
}

KitBatchCard.propTypes = {
  batch: PropTypes.shape({
    id: PropTypes.number,
    batch_number: PropTypes.string.isRequired,
    name: PropTypes.string,
    created_at: PropTypes.string,
    counts: PropTypes.shape({
      total: PropTypes.number,
      claimed: PropTypes.number,
      unclaimed: PropTypes.number,
      invalidated: PropTypes.number,
    }),
    kit: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      sku: PropTypes.string,
    }),
  }).isRequired,
  onExport: PropTypes.func.isRequired,
};
